/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useHistory} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    Box,
    Container,
    Heading,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    Button,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {InfoCard, useControlledDisclosure} from '../../components/info-card'
import ProfileFields from '../../components/forms/profile-fields'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAccountEdit} from '../../contexts/account-edit-context'
import CloseAccountModal from '../../components/close-account-modal'
import {useCustomToast} from '../../contexts/custom-toast-context'
import usedDeviceDetection from '../../hooks/use-device-detection'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import {usePhoneValidation} from '../../integrations/melissa/hooks/use-melissa'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {SITE_COUNTRY_MAP} from '../../constants'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'

const DEFAULT_CUSTOMER_NAME = 'Customer'
const DATA_TEST_ID_PREFIX = 'profile-card'

/**
 * This is a specialized Skeleton component that which uses the customers authtype as the
 * `isLoaded` state. It also will revert it's provided size (height, width) when the loaded
 * state changes. This allows you to have skeletons of a specific size, but onece loaded
 * the bounding element will affect the contents size.
 */
// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})

Skeleton.displayName = 'Skeleton'

const ProfileCard = ({pageHeadingRef}) => {
    const intl = useIntl()
    const {showCustomToast} = useCustomToast()
    const {data: customer, refetch: refetchCurrentCustomer} = useCurrentCustomer()
    const {isRegistered, customerId} = customer
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const {
        isOpen: isOpenCloseAccountModal,
        onOpen: onOpenCloseAccountModal,
        onClose: onCloseCloseAccountModal
    } = useDisclosure()
    const {fetchPhoneValidation} = usePhoneValidation()
    const {site} = useMultiSite()
    const defaultCountry = SITE_COUNTRY_MAP[site?.id] || 'CA'

    const device = usedDeviceDetection()

    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')

    const {startEditing, stopEditing, setFormDirty} = useAccountEdit()

    const form = useForm({
        defaultValues: {
            firstName: customer?.firstName,
            lastName: customer?.lastName !== DEFAULT_CUSTOMER_NAME ? customer?.lastName : '',
            email: customer?.email,
            phone: customer?.phoneHome,
            loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
            birthDay: customer?.c_birthdayFormatted
        }
    })

    const resetFormFromCustomer = useCallback(() => {
        form.reset({
            firstName: customer.firstName,
            lastName: customer?.lastName !== DEFAULT_CUSTOMER_NAME ? customer?.lastName : '',
            email: customer.email,
            phone: customer.phoneHome,
            loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
            birthDay: customer?.c_birthdayFormatted
        })
    }, [customer, form])

    const profileDrawer = useControlledDisclosure({
        onOpening: resetFormFromCustomer,
        onClosing: resetFormFromCustomer
    })

    useEffect(() => {
        form.reset({
            firstName: customer.firstName,
            lastName: customer?.lastName !== DEFAULT_CUSTOMER_NAME ? customer?.lastName : '',
            email: customer.email,
            phone: customer.phoneHome,
            loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
            birthDay: customer?.c_birthdayFormatted
        })
    }, [
        customer?.firstName,
        customer?.lastName,
        customer?.email,
        customer?.phoneHome,
        customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
        customer?.c_birthdayFormatted
    ])

    useEffect(() => {
        if (profileDrawer.isOpen) {
            startEditing()
        } else {
            stopEditing()
        }
    }, [profileDrawer.isOpen, startEditing, stopEditing])

    useEffect(() => {
        if (profileDrawer.isOpen) {
            setFormDirty(form.formState.isDirty)
        }
    }, [profileDrawer.isOpen, form.formState.isDirty, setFormDirty])

    const submit = async (values) => {
        try {
            form.clearErrors()

            const body = {
                firstName: values.firstName,
                lastName: values.lastName,
                phoneHome: values.phone,
                email: values.email,
                login: values.email,
                c_device: device
            }

            const normalizedPhone = (body.phoneHome || '').replace(/[^\d]/g, '')

            const result = await fetchPhoneValidation({
                phoneNumber: normalizedPhone,
                countryCode: defaultCountry,
                shouldFetch: true
            })

            const isValidPhone = result?.phoneValidationResult?.isValidPhone ?? true

            if (!isValidPhone) {
                form.setError('phone', {
                    type: 'manual',
                    message: intl.formatMessage({
                        defaultMessage: 'Invalid phone number',
                        id: 'address.phone.invalid'
                    })
                })

                return
            }

            form.clearErrors('phone')

            const isValidDateISO = /^\d{4}-\d{2}-\d{2}$/.test(values.birthDay)
            if (isValidDateISO) {
                body.birthday = values.birthDay
            }

            updateCustomerMutation.mutate(
                {
                    parameters: {customerId, c_brand: 'Garage'},
                    body
                },
                {
                    onSuccess: async () => {
                        await refetchCurrentCustomer()
                        profileDrawer.setOpen(false)
                        showCustomToast({
                            message: intl.formatMessage({
                                defaultMessage: 'The changes have been saved',
                                id: 'account.address_book.success_message'
                            })
                        })
                        pageHeadingRef?.current?.focus()
                    }
                }
            )
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
            sendViewErrorEvent(error.message)
        }
    }

    const customerName = `${customer?.firstName || ''}${
        customer?.lastName === DEFAULT_CUSTOMER_NAME ? '' : ' ' + customer?.lastName
    }`

    const notProvidedMessage = (
        <FormattedMessage id="profile_details.not_provided" defaultMessage="Not provided" />
    )

    const summary = (
        <Box data-private>
            {customerName ? <Text variant="infoCardText">{customerName}</Text> : null}
            {customer?.email ? <Text variant="infoCardText">{customer.email}</Text> : null}
            <Text variant="infoCardText">
                {customer.phoneHome || notProvidedMessage}
            </Text>
            <Text variant="infoCardText">
                <FormattedMessage
                    id="profile_details.birthday"
                    defaultMessage="Birthday: {birthday}"
                    values={{
                        birthday: customer.birthday
                            ? customer.birthday.split('T')[0].split('-').reverse().join('/')
                            : notProvidedMessage
                    }}
                />
            </Text>
        </Box>
    )

    return (
        <>
            <InfoCard
                {...profileDrawer}
                id="my-profile"
                data-testid={DATA_TEST_ID_PREFIX}
                title={
                    <FormattedMessage
                        id="contact_info.title.contact_info"
                        defaultMessage="Contact Information"
                    />
                }
                drawerTitle={
                    <FormattedMessage
                        defaultMessage="Update informations"
                        id="contact_info.title.edit"
                    />
                }
                summary={summary}
                isEditable={isRegistered}
                editLabel={<FormattedMessage defaultMessage="Edit" id="toggle_card.action.edit" />}
            >
                <Container p={0} m={0}>
                    <form onSubmit={form.handleSubmit(submit)}>
                        <Stack spacing={{base: 8, lg: 10}}>
                            {form.formState.errors?.global && (
                                <Alert status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text fontSize="sm" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}
                            <ProfileFields form={form} birthDayData={customer?.birthday} />
                            <Button
                                data-testid={`${DATA_TEST_ID_PREFIX}-save-button`}
                                type="submit"
                                isLoading={form.formState.isSubmitting}
                                variant="webPrimary"
                            >
                                <Stack spacing={0} mb="5px">
                                    <FormattedMessage
                                        defaultMessage="Update"
                                        id="contact_info.update_information"
                                    />
                                </Stack>
                            </Button>
                        </Stack>
                    </form>
                </Container>
            </InfoCard>

            <Button
                alignSelf={'flex-start'}
                data-testid={`${DATA_TEST_ID_PREFIX}-close-account-button`}
                variant="closeAccountLink"
                onClick={onOpenCloseAccountModal}
            >
                <FormattedMessage defaultMessage="Delete Account" id="close_account_modal.link" />
            </Button>
            <CloseAccountModal
                isOpen={isOpenCloseAccountModal}
                onClose={onCloseCloseAccountModal}
                onOpen={onOpenCloseAccountModal}
            />
        </>
    )
}

const AccountDetail = () => {
    const headingRef = useRef()

    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    return (
        <Stack data-testid="account-detail-page" spacing={12}>
            <Text
                alignItems="center"
                as="h1"
                textStyle="webH1"
                tabIndex="0"
                ref={headingRef}
                gap={6}
            >
                <FormattedMessage
                    defaultMessage="Personal Information"
                    id="account_detail.title.personal_information"
                />
            </Text>

            <ProfileCard pageHeadingRef={headingRef} />
        </Stack>
    )
}

ProfileCard.propTypes = {
    pageHeadingRef: PropTypes.shape({current: PropTypes.any})
}

AccountDetail.getTemplateName = () => 'account-detail'

export default AccountDetail
